import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import logoFull from "@/assets/logo-full.png";

export default function VerifyParent() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const navigate = useNavigate();
  
  const token = searchParams.get("token");
  const ptaId = searchParams.get("pta");

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus("error");
      setErrorMessage("No verification token provided");
    }
  }, [token]);

  // Handle countdown separately
  useEffect(() => {
    if (status !== "success") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setShouldRedirect(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status]);

  // Handle redirect in a separate effect
  useEffect(() => {
    if (!shouldRedirect) return;

    const doRedirect = () => {
      if (ptaId) {
        navigate(`/portal/${ptaId}?verified=true`, { replace: true });
      } else {
        navigate("/portal?verified=true", { replace: true });
      }
    };

    doRedirect();
  }, [shouldRedirect, navigate, ptaId]);

  const verifyToken = async () => {
    try {
      console.log("[VerifyParent] Calling verify-email-token with token");
      const { data, error } = await supabase.functions.invoke("verify-email-token", {
        body: { token },
      });

      console.log("[VerifyParent] Response:", { data, error });

      if (error) {
        throw error;
      }

      if (data?.valid) {
        // Also update the parent record to mark as verified using RPC or direct update
        if (data.userId) {
          // The verify-email-token function already updates profiles table
          // We just mark success here
          console.log("[VerifyParent] Email verified for user:", data.userId);
        }
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage(data?.error || "Invalid or expired verification link");
      }
    } catch (err: any) {
      console.error("Verification error:", err);
      setStatus("error");
      setErrorMessage(err.message || "An error occurred during verification");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <Card className="w-full max-w-md shadow-elevated">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoFull} alt="PTA Hub" className="h-12" />
          </div>
          {status === "loading" && (
            <>
              <CardTitle className="flex items-center justify-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                Verifying Your Email
              </CardTitle>
              <CardDescription>Please wait while we verify your email address...</CardDescription>
            </>
          )}
          {status === "success" && (
            <>
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-green-700">Email Verified!</CardTitle>
              <CardDescription>
                Your email has been verified. Redirecting to sign in page in {countdown} seconds...
              </CardDescription>
            </>
          )}
          {status === "error" && (
            <>
              <div className="flex justify-center mb-2">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <CardTitle className="text-destructive">Verification Failed</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "success" && (
            <Button asChild className="w-full">
              <Link to={ptaId ? `/portal/${ptaId}?verified=true` : "/portal?verified=true"}>
                Sign In to Your Account
              </Link>
            </Button>
          )}
          {status === "error" && (
            <div className="space-y-3">
              <Button asChild variant="outline" className="w-full">
                <Link to={ptaId ? `/portal/${ptaId}` : "/portal"}>
                  <Mail className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Link>
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Need a new verification link? Register again or contact{" "}
                <a href="mailto:info@ptahub.co.uk" className="text-primary hover:underline">
                  info@ptahub.co.uk
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
